'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useState } from 'react';
import { OutcomeDot } from '@/components/shared/outcome-indicator';
import { AppPopover } from '@/components/ui/app-popover';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { type OutcomeHueName, outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
import { MARKET_TAB_FILTER_TRIGGER_CLASS } from '../market-tab-filter-trigger';
interface OutcomeOption {
  index: number;
  label: string;
  imageUrl?: string | undefined;
}
interface MarketTradersOutcomeFilterProps {
  /** Parent market pubkey — seeds the outcome indicator's pattern lib. */
  marketPubkey: string;
  outcomes: readonly OutcomeOption[];
  /** Currently selected outcome index. `null` = "All outcomes" (market aggregate). */
  selectedIndex: number | null;
  /** Pick a single outcome (or `null` for All). */
  onSelect: (index: number | null) => void;
}

/**
 * Single-select outcome filter for the per-market traders leaderboard.
 */
export function MarketTradersOutcomeFilter(t0) {
  const $ = _c(53);
  const {
    marketPubkey,
    outcomes,
    selectedIndex,
    onSelect
  } = t0;
  const tMarket = useTranslations("market.traders");
  const tTrade = useTranslations("trade");
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== outcomes) {
    t1 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== marketPubkey) {
    t2 = {
      marketPubkey
    };
    $[2] = marketPubkey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const colorClasses = useOutcomeColor(t1, t2);
  let colorByIndex;
  if ($[4] !== colorClasses || $[5] !== outcomes) {
    colorByIndex = {};
    const hueByIndex = {};
    for (let i = 0; i < outcomes.length; i++) {
      const o_0 = outcomes[i];
      const cls = colorClasses[i];
      if (o_0 !== undefined && cls !== undefined) {
        colorByIndex[o_0.index] = outcomeHueToStepVar(cls.hue, 400);
        hueByIndex[o_0.index] = cls.hue;
      }
    }
    $[4] = colorClasses;
    $[5] = outcomes;
    $[6] = colorByIndex;
  } else {
    colorByIndex = $[6];
  }
  if (outcomes.length <= 1) {
    return null;
  }
  let t3;
  if ($[7] !== outcomes || $[8] !== selectedIndex) {
    t3 = selectedIndex !== null ? outcomes.find(o_1 => o_1.index === selectedIndex) : undefined;
    $[7] = outcomes;
    $[8] = selectedIndex;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const selected = t3;
  let t4;
  if ($[10] !== tMarket) {
    t4 = tMarket("outcome_filter_label");
    $[10] = tMarket;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== colorByIndex || $[13] !== selected || $[14] !== tTrade) {
    t5 = <span className="flex min-w-0 items-center gap-2 truncate">{selected === undefined ? <span className="truncate text-foreground-secondary">{tTrade("all_outcomes")}</span> : <><OutcomeDot color={colorByIndex[selected.index] ?? ""} /><span className="truncate">{selected.label}</span></>}</span>;
    $[12] = colorByIndex;
    $[13] = selected;
    $[14] = tTrade;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={ArrowDown01Icon} className="size-4 shrink-0 opacity-50" />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== open || $[18] !== t4 || $[19] !== t5) {
    t7 = <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={t4} data-testid="market-traders-outcome-filter" className={MARKET_TAB_FILTER_TRIGGER_CLASS}>{t5}{t6}</button>;
    $[17] = open;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const trigger = t7;
  const t8 = selectedIndex === null;
  let t9;
  if ($[21] !== onSelect) {
    t9 = () => {
      onSelect(null);
      setOpen(false);
    };
    $[21] = onSelect;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = selectedIndex === null ? "bg-background-tertiary font-medium text-foreground-primary" : "text-foreground-secondary hover:bg-background-tertiary/50 hover:text-foreground-primary";
  let t11;
  if ($[23] !== t10) {
    t11 = cn("flex w-full items-center justify-between px-3 py-2.5 text-left text-body transition-colors", t10);
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== tTrade) {
    t12 = tTrade("all_outcomes");
    $[25] = tTrade;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <span className="truncate">{t12}</span>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== selectedIndex) {
    t14 = selectedIndex === null && <Icon icon={Tick02Icon} className="size-4 shrink-0" />;
    $[29] = selectedIndex;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== t14 || $[34] !== t8 || $[35] !== t9) {
    t15 = <button type="button" aria-pressed={t8} onClick={t9} className={t11}>{t13}{t14}</button>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
    $[34] = t8;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== colorByIndex || $[38] !== onSelect || $[39] !== outcomes || $[40] !== selectedIndex) {
    let t17;
    if ($[42] !== colorByIndex || $[43] !== onSelect || $[44] !== selectedIndex) {
      t17 = outcome => {
        const isSelected = selectedIndex === outcome.index;
        const dotColor = colorByIndex[outcome.index] ?? "";
        return <button key={outcome.index} type="button" aria-pressed={isSelected} data-testid={`market-traders-outcome-item-${outcome.index}`} onClick={() => {
          onSelect(outcome.index);
          setOpen(false);
        }} className={cn("flex w-full items-center justify-between px-3 py-2.5 text-left text-body transition-colors", isSelected ? "bg-background-tertiary font-medium text-foreground-primary" : "text-foreground-secondary hover:bg-background-tertiary/50 hover:text-foreground-primary")}><span className="flex min-w-0 items-center gap-2.5 truncate"><OutcomeDot color={dotColor} /><span className="truncate">{outcome.label}</span></span>{isSelected && <Icon icon={Tick02Icon} className="size-4 shrink-0" />}</button>;
      };
      $[42] = colorByIndex;
      $[43] = onSelect;
      $[44] = selectedIndex;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    t16 = outcomes.map(t17);
    $[37] = colorByIndex;
    $[38] = onSelect;
    $[39] = outcomes;
    $[40] = selectedIndex;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[46] !== t15 || $[47] !== t16) {
    t17 = <div className="max-h-56 overflow-y-auto">{t15}{t16}</div>;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  const content = t17;
  let t18;
  if ($[49] !== content || $[50] !== open || $[51] !== trigger) {
    t18 = <AppPopover open={open} onOpenChange={setOpen} trigger={trigger} content={content} side="bottom" align="end" sideOffset={4} contentClassName="w-[var(--radix-popover-trigger-width)] min-w-56 bg-background-primary p-0" />;
    $[49] = content;
    $[50] = open;
    $[51] = trigger;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.index
  };
}